import { useEffect, useRef, useState } from "react";
import digitDrop from "../assets/DigitDrop.png";
import timeFlux from "../assets/projectImages/TF.png";
import twoArrows from "../assets/TwoArrows.png";
import perimeter from "../assets/PerimeterTheGame.png";
import lavaRiver from "../assets/LAVARIVER.png";
import feedTheBear from "../assets/FeedTheBear.png";
import polyDrive from "../assets/PolyDrive.png";
import tent from "../assets/Tent.png";
import stockMarket from "../assets/StockMarketSImulator.png";
import recycleNyc from "../assets/RecycleNYC.jpg";
import cpu from "../assets/SINGLECYCLECPU.png";
import throwbacksIcon from "../assets/ThrowbacksIcon.png";

const githubProfile = "https://github.com/nmaswood98";

const apps = [
  {
    slug: "digit-drop",
    title: "Digit Drop",
    year: "2017",
    image: digitDrop,
    accent: "#ff603d",
    ink: "#231310",
    platform: "iOS + Android",
    tools: "Unity 2D · C# · Game Center",
    lead: "A number-matching puzzle game.",
    story: "Combine falling values to hit the target before the board fills. The constraint made every decision legible: one screen, one goal, one loop worth replaying.",
    detail: "Released across two mobile platforms with leaderboards through Apple Game Center and Google Play Games.",
  },
  {
    slug: "time-flux",
    title: "Time Flux",
    year: "2016",
    image: timeFlux,
    artClass: "time-flux-gameplay",
    accent: "#796bff",
    ink: "#16122c",
    platform: "iOS + Android",
    tools: "Unity 2D · C#",
    lead: "A clock-based reflex game.",
    story: "Time Flux asks players to match the arrow with the time at the top. The interface is the mechanic—nothing sits between the player and the next decision.",
    detail: "Designed, built, and released independently in 2016 using Unity 2D.",
  },
  {
    slug: "two-arrows",
    title: "Two Arrows",
    year: "2015",
    image: twoArrows,
    accent: "#36c4a8",
    ink: "#08251f",
    platform: "iOS",
    tools: "Objective-C · Cocos2D",
    lead: "A two-sided arrow-matching game.",
    story: "Players match falling arrows by tapping the correct side of the screen. Rush and Mind Bend modes pushed the same input into different kinds of pressure.",
    detail: "The public Objective-C repository preserves the game scenes, tutorials, statistics, and mode-specific logic.",
    github: "https://github.com/nmaswood98/Two-Arrows",
  },
  {
    slug: "perimeter",
    title: "Perimeter the Game",
    year: "2015",
    image: perimeter,
    accent: "#ef6ba2",
    ink: "#321322",
    platform: "iOS",
    tools: "Objective-C · SpriteKit",
    lead: "A one-tap timing game.",
    story: "Two perimeters move in and out of alignment. The whole game lives in the tension just before they overlap—and the impulse to try once more when they do not.",
    detail: "A minimal SpriteKit prototype carried all the way through to a finished App Store release.",
  },
  {
    slug: "lava-river",
    title: "Lava River",
    year: "2014",
    image: lavaRiver,
    accent: "#f5b936",
    ink: "#281d04",
    platform: "iOS",
    tools: "Objective-C · SpriteKit",
    lead: "An endless rock-hopping arcade game.",
    story: "Tap the rocks, keep moving, and survive the trip downstream. Lava River was one of the earliest complete products in this archive.",
    detail: "Built natively with Apple’s SpriteKit and released on the App Store in 2014.",
  },
  {
    slug: "feed-the-bear",
    title: "Feed the Bear",
    year: "2014",
    image: feedTheBear,
    accent: "#30c8d4",
    ink: "#06262a",
    platform: "iOS",
    tools: "Objective-C · SpriteKit",
    lead: "An arcade game about catching falling fish.",
    story: "Players tap the bears while fish fall through the scene. Normal, Blitz, and motion-based modes vary the pace and controls around the same central mechanic.",
    detail: "The public Objective-C repository includes the gameplay modes, tutorials, scoring, and scene logic.",
    github: "https://github.com/nmaswood98/Feed-The-Bear",
  },
];

const sideProjects = [
  {
    title: "Polydrive.io",
    label: "Multiplayer web game",
    image: polyDrive,
    text: "A real-time multiplayer browser game powered by Node.js, PixiJS, and WebSockets.",
    href: "https://github.com/nmaswood98/Polydrive.io",
  },
  {
    title: "Tent",
    label: "iOS prototype",
    image: tent,
    text: "A location-aware SwiftUI concept for sharing photos and drawings with people nearby.",
    href: githubProfile,
  },
];

const collegeProjects = [
  { title: "Stock Market Simulator", meta: "React · Redux · TypeScript", image: stockMarket, href: "https://github.com/StockMarketSimulator-CSC-59867-03/SMSimulator_Website" },
  { title: "Recycle NYC", meta: "D3.js · Data visualization", image: recycleNyc, href: "https://github.com/nmaswood98/RecycleNYC-2" },
  { title: "Single Cycle CPU", meta: "VHDL · ModelSim · FPGA", image: cpu, href: "https://docs.google.com/document/d/1MUviqyFwQEvBrgUFJOnBp6lTJAysvJqtYxpsL5IxBY4/edit?usp=sharing" },
];

function Arrow({ diagonal = false }) {
  return <span aria-hidden="true">{diagonal ? "↗" : "→"}</span>;
}

function ExternalLink({ href, children, className = "text-link" }) {
  return <a className={className} href={href} target="_blank" rel="noreferrer">{children} <Arrow diagonal /></a>;
}

function App() {
  const [activeApp, setActiveApp] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const archiveRef = useRef(null);

  useEffect(() => {
    const chapters = [...document.querySelectorAll(".story-chapter")];
    let scrollFrame = 0;

    const updateScrollState = () => {
      scrollFrame = 0;
      setScrolled(window.scrollY > 24);

      const focusLine = window.innerHeight * 0.52;
      let nextApp = 0;
      chapters.forEach((chapter, index) => {
        if (chapter.getBoundingClientRect().top <= focusLine) nextApp = index;
      });
      setActiveApp((current) => (current === nextApp ? current : nextApp));
    };

    const onScroll = () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    const revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")),
      { threshold: 0.12 },
    );
    document.querySelectorAll(".reveal").forEach((element) => revealObserver.observe(element));

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      revealObserver.disconnect();
    };
  }, []);

  const goTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const currentApp = apps[activeApp];

  return (
    <>
      <header className={scrolled ? "site-header is-scrolled" : "site-header"}>
        <a className="wordmark" href="#top" aria-label="NM, archive home">NM<span>·</span></a>
        <button className="menu-button" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>{menuOpen ? "Close" : "Menu"}</button>
        <nav className={menuOpen ? "nav-links is-open" : "nav-links"} aria-label="Main navigation">
          <button onClick={() => goTo("archive")}>App archive</button>
          <button onClick={() => goTo("now")}>What’s new</button>
          <ExternalLink href={githubProfile}>GitHub</ExternalLink>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-copy reveal is-visible">
            <p className="eyebrow"><span>Independent mobile game archive</span><span>2014—2017</span></p>
            <h1>App Store<br /><em>archive.</em></h1>
            <p className="hero-intro">Six independently released mobile games from 2014–2017. The original releases are no longer available; this archive documents how they played, what they were built with, and where source code is available.</p>
            <div className="hero-actions">
              <button className="button button-dark" onClick={() => archiveRef.current?.scrollIntoView({ behavior: "smooth" })}>Explore the apps <Arrow /></button>
              <ExternalLink href={githubProfile} className="button button-ghost">Open GitHub</ExternalLink>
            </div>
          </div>
          <div className="app-stack reveal is-visible" aria-label="Six app artworks">
            {apps.map((app, index) => (
              <button key={app.slug} className={`stack-card stack-${index + 1}`} onClick={() => goTo(app.slug)} aria-label={`Jump to ${app.title}`}>
                <span className={`stack-art ${app.artClass || ""}`}><img src={app.image} alt="" /></span><span>{app.title}</span>
              </button>
            ))}
            <span className="stack-caption">Tap a card to jump in</span>
          </div>
          <div className="scroll-note"><span>Scroll through the archive</span><i /></div>
        </section>

        <section className="archive-intro" id="archive" ref={archiveRef}>
          <div className="section-heading reveal">
            <div><p className="kicker">01 — Retired releases</p><h2>Six App Store games.</h2></div>
            <div className="archive-context">
              <p>These games are preserved here as product history. They had their run on the App Store, but are <strong>no longer available for download.</strong></p>
              <span className="status-key"><i /> Archived release</span>
            </div>
          </div>
        </section>

        <section className="scrolly" style={{ "--story-accent": currentApp.accent, "--story-ink": currentApp.ink }}>
          <div className="story-stage-wrap">
            <div className="story-stage">
              <div className="stage-grid" aria-hidden="true" />
              <div className="stage-topline"><span>{String(activeApp + 1).padStart(2, "0")} / {String(apps.length).padStart(2, "0")}</span><span>{currentApp.year}</span></div>
              <div className="art-orbit" aria-hidden="true"><i /><i /><i /></div>
              <div className="stage-images">
                {apps.map((app, index) => (
                  <div key={app.slug} className={`stage-image ${index === activeApp ? "is-active" : ""} ${app.artClass || ""}`}>
                    <img src={app.image} alt={index === activeApp ? `${app.title} artwork` : ""} />
                  </div>
                ))}
              </div>
              <div className="stage-title" aria-live="polite"><span>{currentApp.year}</span><strong data-app={currentApp.slug}>{currentApp.title}</strong></div>
              <span className="retired-stamp">Retired from the App Store</span>
              <div className="stage-progress" aria-label={`App ${activeApp + 1} of ${apps.length}`}>
                {apps.map((app, index) => <button className={index === activeApp ? "is-active" : ""} key={app.slug} onClick={() => goTo(app.slug)} aria-label={`Jump to ${app.title}`} />)}
              </div>
            </div>
          </div>

          <div className="story-copy">
            {apps.map((app, index) => (
              <article className="story-chapter" id={app.slug} data-index={index} key={app.slug}>
                <div className="chapter-number"><span>{String(index + 1).padStart(2, "0")}</span><i /></div>
                <p className="chapter-meta">{app.year} · {app.platform}</p>
                <h3>{app.lead}</h3>
                <p className="chapter-story">{app.story}</p>
                <p className="chapter-detail">{app.detail}</p>
                <div className="chapter-footer">
                  <span>{app.tools}</span>
                  {app.github && <ExternalLink href={app.github}>View source</ExternalLink>}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="now-section" id="now">
          <div className="now-orbit orbit-one" aria-hidden="true" /><div className="now-orbit orbit-two" aria-hidden="true" />
          <div className="now-copy reveal">
            <p className="kicker">02 — Current releases</p>
            <h2>Extra Krispy Labs.</h2>
            <p>Current mobile apps are published by <strong>Extra Krispy Labs</strong> for iOS and Android.</p>
            <div className="now-actions">
              <ExternalLink href="https://extrakrispylabs.com/" className="button button-light">Visit Extra Krispy Labs</ExternalLink>
              <ExternalLink href="https://apps.apple.com/us/app/throwbacks/id6743415817" className="text-link light-link">View Throwbacks on the App Store</ExternalLink>
            </div>
          </div>
          <a className="throwbacks-card reveal" href="https://apps.apple.com/us/app/throwbacks/id6743415817" target="_blank" rel="noreferrer">
            <img className="throwbacks-icon" src={throwbacksIcon} alt="Throwbacks app icon" />
            <div><span className="live-label"><i /> Live now</span><h3>Throwbacks!</h3><p>A photo guessing game that turns your own camera roll into a trip through time.</p></div>
            <Arrow diagonal />
          </a>
        </section>

        <section className="experiments-section">
          <div className="section-heading compact reveal">
            <div><p className="kicker">03 — Additional work</p><h2>Other projects.</h2></div>
            <p>Selected prototypes and open-source projects.</p>
          </div>
          <div className="side-grid">
            {sideProjects.map((project) => (
              <a className="side-card reveal" href={project.href} target="_blank" rel="noreferrer" key={project.title}>
                <div className="side-image"><img src={project.image} alt="" /></div>
                <div className="side-copy"><span>{project.label}</span><h3>{project.title}</h3><p>{project.text}</p></div><Arrow diagonal />
              </a>
            ))}
          </div>
        </section>

        <section className="college-section reveal">
          <details>
            <summary><span><small>Earlier coursework</small> College projects</span><span className="summary-action">Open archive <i>+</i></span></summary>
            <div className="college-grid">
              {collegeProjects.map((project) => (
                <a href={project.href} target="_blank" rel="noreferrer" key={project.title}>
                  <img src={project.image} alt="" /><span><strong>{project.title}</strong><small>{project.meta}</small></span><Arrow diagonal />
                </a>
              ))}
            </div>
          </details>
        </section>
      </main>

      <footer>
        <div><p>Current mobile releases</p><h2>Extra Krispy<br />Labs.</h2></div>
        <div className="footer-links">
          <ExternalLink href={githubProfile}>GitHub</ExternalLink>
          <ExternalLink href="https://extrakrispylabs.com/">Extra Krispy Labs</ExternalLink>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top ↑</button>
        </div>
        <p className="footer-note">Independent mobile game archive · © {new Date().getFullYear()}</p>
      </footer>
    </>
  );
}

export default App;
